import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Eye, Search, Filter, Download, Upload, FileSpreadsheet, Calendar, BookOpen, Users, Mail, Phone, MapPin } from 'lucide-react';
import * as XLSX from 'xlsx';
import { exportEnseignantsToExcel } from '../services/exportService';

const btnBase = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.75rem 1.25rem',
  border: 'none',
  borderRadius: '12px',
  fontWeight: '600',
  cursor: 'pointer'
};
const btnOutline = { ...btnBase, background: 'white', color: '#7c3aed', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const btnPrimary = { ...btnBase, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white' };

const Enseignants = () => {
  const [enseignants, setEnseignants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // add, edit, view
  const [selectedEnseignant, setSelectedEnseignant] = useState(null);
  const [showDisponibilites, setShowDisponibilites] = useState(false);
  const [stats, setStats] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    dateNaissance: '',
    lieuNaissance: '',
    sexe: '',
    nationalite: 'Marocaine',
    cin: '',
    adresse: '',
    ville: '',
    specialite: '',
    diplome: '',
    grade: 'Professeur',
    dateEmbauche: '',
    statut: 'Permanent',
    departement: '',
    bureau: '',
    tauxHoraire: '',
    tauxHoraireRattrapage: '',
    matieres: []
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // La scolarité gère les enseignants comme la direction : création, modification et
  // suppression, au même titre que l administrateur et le directeur.
  const peutGererEnseignants = ['ADMIN', 'DIRECTEUR', 'SCOLARITE'].includes(user?.role);

  const IMPORT_ENSEIGNANTS_ACTIF = false;

  const itemsPerPage = 20;

  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.get('/api/enseignants', { headers });
      setEnseignants(res.data || []);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les enseignants');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get('/api/enseignants/stats', { headers });
      setStats(res.data);
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  const flash = (msg, duree = 5000) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), duree);
  };

  const handleExportExcel = () => {
    if (filteredEnseignants.length === 0) {
      flash('Aucun enseignant à exporter', 3000);
      return;
    }
    exportEnseignantsToExcel(filteredEnseignants);
  };

  const telechargerModele = () => {
    const modele = [{
      'Nom': 'Exemple',
      'Prénom': 'Mohamed',
      'Email': 'mohamed.exemple@sup2i.ac',
      'Téléphone': '0600000000',
      'Spécialité': 'Informatique',
      'Grade': 'Professeur',
      'Statut': 'Permanent',
      'Département': 'Génie Informatique'
    }];
    const ws = XLSX.utils.json_to_sheet(modele);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Enseignants');
    XLSX.writeFile(wb, 'modele_import_enseignants.xlsx');
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setImporting(true);
      const buffer = await file.arrayBuffer();
      const classeur = XLSX.read(buffer, { type: 'array' });
      const feuille = classeur.Sheets[classeur.SheetNames[0]];
      const lignes = XLSX.utils.sheet_to_json(feuille, { defval: '' });
      const colonnesDetectees = lignes.length > 0 ? Object.keys(lignes[0]) : [];

      const normaliserCle = (s) => String(s)
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().replace(/[^a-z0-9]/g, '');

      const lire = (l, ...cles) => {
        for (const cle of cles) {
          const cible = normaliserCle(cle);
          for (const k of Object.keys(l)) {
            if (normaliserCle(k) === cible) {
              const v = l[k];
              if (v !== undefined && v !== null && String(v).trim() !== '') {
                return String(v).trim();
              }
            }
          }
        }
        return '';
      };

      const enseignantsImport = lignes.map(l => ({
        nom: lire(l, 'Nom', 'nom', 'NOM'),
        prenom: lire(l, 'Prénom', 'Prenom', 'prenom', 'PRENOM'),
        email: lire(l, 'Email', 'email', 'EMAIL'),
        telephone: lire(l, 'Téléphone', 'Telephone', 'telephone'),
        specialite: lire(l, 'Spécialité', 'Specialite', 'specialite'),
        grade: lire(l, 'Grade', 'grade'),
        statut: lire(l, 'Statut', 'statut'),
        departement: lire(l, 'Département', 'Departement', 'departement')
      })).filter(x => x.nom || x.prenom);

      if (enseignantsImport.length === 0) {
        flash(`Fichier vide ou colonnes non reconnues. Colonnes détectées : ${colonnesDetectees.join(', ') || 'aucune'}. Colonnes attendues : Nom, Prénom (au choix : Email, Téléphone, Spécialité, Grade, Statut, Département).`, 12000);
        return;
      }

      const token = localStorage.getItem('token');
      const res = await axios.post('/api/enseignants/import', { enseignants: enseignantsImport }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      const nbErr = d.errors?.length || 0;
      let texte = `${nbErr === 0 ? 'Import réussi' : 'Import terminé'} : ${d.created?.length || 0} compte(s) créé(s), ${d.updated?.length || 0} réinitialisé(s), ${nbErr} erreur(s).`;
      if (nbErr > 0) {
        texte += ' ' + d.errors.slice(0, 3).map(x => `Ligne ${x.ligne} : ${x.erreur}`).join(' | ');
      }
      flash(texte, 12000);
      await loadData();
      await loadStats();
    } catch (err) {
      console.error('Erreur import:', err);
      flash(err.response?.data?.error || 'Erreur lors de l\'import du fichier', 6000);
    } finally {
      setImporting(false);
    }
  };

  const filteredEnseignants = enseignants.filter(e => {
    const matchesSearch = searchTerm === '' || 
      `${e.nom} ${e.prenom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.matricule?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredEnseignants.length / itemsPerPage);
  const paginatedEnseignants = filteredEnseignants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleAdd = async () => {
    if (!formData.nom || !formData.prenom || !formData.email) {
      setMessage('Veuillez remplir les champs obligatoires');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post('/api/enseignants', formData, { headers });
      await loadData();
      await loadStats();
      setShowModal(false);
      resetForm();
      setMessage('Enseignant ajouté avec succès');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Erreur lors de l\'ajout');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`/api/enseignants/${selectedEnseignant.matricule}`, formData, { headers });
      await loadData();
      await loadStats();
      setShowModal(false);
      resetForm();
      setMessage('Enseignant modifié avec succès');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Erreur lors de la modification');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleDelete = async (enseignant) => {
    if (!window.confirm(`Supprimer définitivement ${enseignant.nom} ${enseignant.prenom} ?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`/api/enseignants/${enseignant.matricule}`, { headers });
      await loadData();
      await loadStats();
      setMessage('Enseignant supprimé avec succès');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Erreur lors de la suppression');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      dateNaissance: '',
      lieuNaissance: '',
      sexe: '',
      nationalite: 'Marocaine',
      cin: '',
      adresse: '',
      ville: '',
      specialite: '',
      diplome: '',
      grade: 'Professeur',
      dateEmbauche: '',
      statut: 'Permanent',
      departement: '',
      bureau: '',
      matieres: []
    });
    setSelectedEnseignant(null);
  };

  const openAddModal = () => {
    setModalMode('add');
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (enseignant) => {
    setModalMode('edit');
    setSelectedEnseignant(enseignant);
    setFormData({
      nom: enseignant.nom || '',
      prenom: enseignant.prenom || '',
      email: enseignant.email || '',
      telephone: enseignant.telephone || '',
      dateNaissance: enseignant.dateNaissance?.split('T')[0] || '',
      lieuNaissance: enseignant.lieuNaissance || '',
      sexe: enseignant.sexe || '',
      nationalite: enseignant.nationalite || 'Marocaine',
      cin: enseignant.cin || '',
      adresse: enseignant.adresse || '',
      ville: enseignant.ville || '',
      specialite: enseignant.specialite || '',
      diplome: enseignant.diplome || '',
      grade: enseignant.grade || 'Professeur',
      dateEmbauche: enseignant.dateEmbauche?.split('T')[0] || '',
      statut: enseignant.statut || 'Permanent',
      departement: enseignant.departement || '',
      bureau: enseignant.bureau || '',
      tauxHoraire: enseignant.tauxHoraire ?? '',
      tauxHoraireRattrapage: enseignant.tauxHoraireRattrapage ?? '',
      matieres: enseignant.matieres || []
    });
    setShowModal(true);
  };

  const openViewModal = (enseignant) => {
    setModalMode('view');
    setSelectedEnseignant(enseignant);
    setShowModal(true);
  };

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'Professeur': return { bg: '#dbeafe', color: '#1e40af' };
      case 'Maître de Conférences': return { bg: '#dcfce7', color: '#166534' };
      case 'Professeur Assistant': return { bg: '#fef3c7', color: '#92400e' };
      default: return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div className="loading-spinner"></div>
        <p>Chargement des enseignants...</p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #f3e8ff 100%)',
      padding: '1.5rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* En-tête */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>👨‍🏫 Gestion des Enseignants</h1>
            <p style={{ color: '#6b7280' }}>{enseignants.length} enseignant(s) - {stats?.total || 0} au total</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {IMPORT_ENSEIGNANTS_ACTIF && (
              <>
                <button onClick={handleExportExcel} style={btnOutline} title="Exporter la liste au format Excel">
                  <Download size={18} />
                  Exporter Excel
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  style={{ ...btnOutline, opacity: importing ? 0.6 : 1, cursor: importing ? 'wait' : 'pointer' }}
                  title="Importer des enseignants depuis un fichier Excel (crée la fiche et le compte de connexion)"
                >
                  <Upload size={18} />
                  {importing ? 'Import...' : 'Importer Excel'}
                </button>
                <button onClick={telechargerModele} style={btnOutline} title="Télécharger le modèle Excel">
                  <FileSpreadsheet size={18} />
                  Modèle Excel
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportFile}
                  style={{ display: 'none' }}
                />
              </>
            )}
            {peutGererEnseignants && (
              <button onClick={openAddModal} style={btnPrimary}>
                <Plus size={18} />
                Nouvel Enseignant
              </button>
            )}
          </div>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            background: (message.includes('succès') || message.includes('réussi')) ? '#dcfce7' : '#fef3c7',
            color: (message.includes('succès') || message.includes('réussi')) ? '#166534' : '#92400e',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            {message}
          </div>
        )}

        {/* Statistiques */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ background: 'white', padding: '1rem', borderRadius: '12px', textAlign: 'center' }}>
              <Users size={24} style={{ margin: '0 auto 0.5rem', color: '#3b82f6' }} />
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.total}</p>
              <p>Total enseignants</p>
            </div>
            {stats.parGrade?.map(g => (
              <div key={g._id} style={{ background: 'white', padding: '1rem', borderRadius: '12px', textAlign: 'center' }}>
                <BookOpen size={24} style={{ margin: '0 auto 0.5rem', color: '#8b5cf6' }} />
                <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{g.count}</p>
                <p>{g._id}</p>
              </div>
            ))}
          </div>
        )}

        {/* Recherche */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem',
          flexWrap: 'wrap'
        }}>
          <div style={{
            flex: 1,
            background: 'white',
            borderRadius: '12px',
            padding: '0.625rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Search size={18} color="#6b7280" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, matricule ou email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.875rem' }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
            )}
          </div>
        </div>

        {/* Tableau */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          overflowX: 'auto',
          boxShadow: '0 4px 6px rgba(0,0,0,0.07)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Matricule</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Nom Complet</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Spécialité</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Grade</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Statut</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEnseignants.map((e) => {
                const gradeStyle = getGradeColor(e.grade);
                return (
                  <tr key={e.matricule} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#3b82f6' }}>{e.matricule}</td>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{e.nom} {e.prenom}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{e.specialite}</div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Mail size={14} color="#6b7280" />
                        <span>{e.email}</span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        background: '#dbeafe',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        color: '#1e40af'
                      }}>
                        {e.specialite || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        background: gradeStyle.bg,
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        color: gradeStyle.color
                      }}>
                        {e.grade}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        background: e.statut === 'Permanent' ? '#dcfce7' : '#fef3c7',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        color: e.statut === 'Permanent' ? '#166534' : '#92400e'
                      }}>
                        {e.statut}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => openViewModal(e)}
                          style={{ padding: '0.5rem', background: '#dbeafe', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#3b82f6' }}
                          title="Voir"
                        >
                          <Eye size={16} />
                        </button>
                        {peutGererEnseignants && (
                          <>
                            <button
                              onClick={() => openEditModal(e)}
                              style={{ padding: '0.5rem', background: '#dcfce7', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#10b981' }}
                              title="Modifier"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(e)}
                              style={{ padding: '0.5rem', background: '#fee2e2', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#ef4444' }}
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                   </tr>
                );
              })}
              {filteredEnseignants.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                    Aucun enseignant trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                background: currentPage === 1 ? '#f3f4f6' : '#3b82f6',
                color: currentPage === 1 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ◀ Précédent
            </button>
            <span style={{ padding: '0.5rem 1rem', background: '#e5e7eb', borderRadius: '8px' }}>
              Page {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                background: currentPage === totalPages ? '#f3f4f6' : '#3b82f6',
                color: currentPage === totalPages ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Suivant ▶
            </button>
          </div>
        )}

        {/* Modal Ajout/Modification/View */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              width: '90%',
              maxWidth: modalMode === 'view' ? '600px' : '550px',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '25px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2>
                  {modalMode === 'add' && '➕ Ajouter un enseignant'}
                  {modalMode === 'edit' && '✏️ Modifier l\'enseignant'}
                  {modalMode === 'view' && '👁️ Détails de l\'enseignant'}
                </h2>
                <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✕</button>
              </div>

              {modalMode === 'view' && selectedEnseignant && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '20px' }}>
                    <p style={{ fontWeight: 'bold' }}>Matricule:</p><p>{selectedEnseignant.matricule}</p>
                    <p style={{ fontWeight: 'bold' }}>Nom complet:</p><p>{selectedEnseignant.nom} {selectedEnseignant.prenom}</p>
                    <p style={{ fontWeight: 'bold' }}>Email:</p><p>{selectedEnseignant.email}</p>
                    <p style={{ fontWeight: 'bold' }}>Téléphone:</p><p>{selectedEnseignant.telephone || '-'}</p>
                    <p style={{ fontWeight: 'bold' }}>Spécialité:</p><p>{selectedEnseignant.specialite || '-'}</p>
                    <p style={{ fontWeight: 'bold' }}>Grade:</p><p>{selectedEnseignant.grade}</p>
                    <p style={{ fontWeight: 'bold' }}>Statut:</p><p>{selectedEnseignant.statut}</p>
                    <p style={{ fontWeight: 'bold' }}>Département:</p><p>{selectedEnseignant.departement || '-'}</p>
                    <p style={{ fontWeight: 'bold' }}>Bureau:</p><p>{selectedEnseignant.bureau || '-'}</p>
                    <p style={{ fontWeight: 'bold' }}>Taux horaire:</p><p>{selectedEnseignant.tauxHoraire != null ? `${selectedEnseignant.tauxHoraire} MAD` : '-'}</p>
                    <p style={{ fontWeight: 'bold' }}>Taux horaire rattrapage:</p><p>{selectedEnseignant.tauxHoraireRattrapage != null ? `${selectedEnseignant.tauxHoraireRattrapage} MAD` : (selectedEnseignant.tauxHoraire != null ? `${selectedEnseignant.tauxHoraire} MAD` : '-')}</p>
                    <p style={{ fontWeight: 'bold' }}>Date d'embauche:</p><p>{selectedEnseignant.dateEmbauche ? new Date(selectedEnseignant.dateEmbauche).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
              )}

              {(modalMode === 'add' || modalMode === 'edit') && (
                <form onSubmit={(e) => { e.preventDefault(); modalMode === 'add' ? handleAdd() : handleUpdate(); }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label>Nom *</label>
                      <input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Prénom *</label>
                      <input type="text" value={formData.prenom} onChange={(e) => setFormData({...formData, prenom: e.target.value})} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Email *</label>
                      <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Téléphone</label>
                      <input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Spécialité</label>
                      <input type="text" value={formData.specialite} onChange={(e) => setFormData({...formData, specialite: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Grade</label>
                      <select value={formData.grade} onChange={(e) => setFormData({...formData, grade: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}>
                        <option value="Professeur">Professeur</option>
                        <option value="Maître de Conférences">Maître de Conférences</option>
                        <option value="Professeur Assistant">Professeur Assistant</option>
                        <option value="Vacataire">Vacataire</option>
                        <option value="Doctorant">Doctorant</option>
                      </select>
                    </div>
                    <div>
                      <label>Statut</label>
                      <select value={formData.statut} onChange={(e) => setFormData({...formData, statut: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}>
                        <option value="Permanent">Permanent</option>
                        <option value="Vacataire">Vacataire</option>
                        <option value="Contractuel">Contractuel</option>
                      </select>
                    </div>
                    <div>
                      <label>Département</label>
                      <input type="text" value={formData.departement} onChange={(e) => setFormData({...formData, departement: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Taux horaire (MAD)</label>
                      <input type="number" min="0" step="0.01" value={formData.tauxHoraire} onChange={(e) => setFormData({...formData, tauxHoraire: e.target.value})} placeholder="Non défini" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Taux horaire rattrapage (MAD)</label>
                      <input type="number" min="0" step="0.01" value={formData.tauxHoraireRattrapage} onChange={(e) => setFormData({...formData, tauxHoraireRattrapage: e.target.value})} placeholder="Utilise le taux normal" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Date d'embauche</label>
                      <input type="date" value={formData.dateEmbauche} onChange={(e) => setFormData({...formData, dateEmbauche: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                    <div>
                      <label>Bureau</label>
                      <input type="text" value={formData.bureau} onChange={(e) => setFormData({...formData, bureau: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 20px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Annuler</button>
                    <button type="submit" style={{ padding: '10px 20px', background: '#8b5cf6', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer' }}>
                      {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Enseignants;


